// The publishing flow of the Visual Editor's inline panel (school-side trial
// feedback, approved 2026-09-27):
//
//   edit ──Review & publish──▶ review ──Publish to my website──▶ progress
//     ▲                          │                                 │
//     └──────Keep editing────────┘        live (+ confirmed on the page) ─▶ success
//                                         needs_review ─▶ under_review
//                                           (applied + commit: waits for them to be live first)
//                                         failed / deploy_failed ─▶ failed
//
// progress: POST /api/edit-requests (source inline) → POST /:id/publish, then
// GET /:id every POLL_MS. The response's `publishProgress` ({ step, steps })
// and `publishState` drive the step list. When it is Live, the panel reloads
// each changed page (cache-busting) and asks the page (NEXSAV_INLINE_VERIFY)
// whether it shows the changes, retrying for about a minute, before it says
// so. A partial publish (needs_review with a commit) waits the same way for
// its applied changes before it reloads the page (see `settle`). Reload
// persistence: `resume` (an in-flight item from GET
// /api/edit-requests/inline-status) opens straight into progress (or, for an
// undo that is running, into success with the undo under way).

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../../lib/api';
import {
  buildCreatePayload, isPublishingState, publishErrorMessage, failureReasonFromChanges, verifiableChanges,
  invertTextChanges, parseServerDate,
} from './inlineEdit';

export const POLL_MS = 3000;
export const VERIFY_ATTEMPTS = 6;
export const VERIFY_RETRY_MS = 10000;
export const VERIFY_WINDOW_MS = 60000;
export const LOST_CONTACT_AFTER = 5;
// A partial publish settles (needs_review) when its commit is PUSHED, before
// Cloudflare has deployed it (about a minute, more when builds queue), so its
// check gets a longer window than the Live one's.
export const REVIEW_WAIT_MS = 240000;
export const REVIEW_WAIT_ATTEMPTS = Math.ceil(REVIEW_WAIT_MS / VERIFY_RETRY_MS) + 1;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Only these readings prove the site shows a change: a text change, or a
// picture whose description changed. A swapped picture with the same
// description looks the same as a cached old file (bridge contract), so a
// publish with one can only get the "can take a minute" wording.
const normAlt = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
export function isConfirmable(change) {
  if (change?.kind === 'text') return true;
  if (change?.kind === 'image') return normAlt(change.before?.alt) !== normAlt(change.after?.alt);
  return false;
}

// VERIFIED reasons that retrying can't fix (the page can't say either way).
const CANNOT_CONFIRM = new Set(['not_found', 'ambiguous', 'invalid']);

// Does the live site show `changes`? For each changed page: reload it with a
// cache-busting parameter, wait for READY (nothing is APPLYed: the list is
// empty by now), then VERIFY. `old_content` (the old version is still being
// served) → reload and ask again every VERIFY_RETRY_MS, at most
// VERIFY_ATTEMPTS times within about VERIFY_WINDOW_MS (`attempts` / `windowMs`
// override both); `not_found` /
// `ambiguous` / `invalid` (or no answer for a change) → stop: it can't be
// confirmed. The first changed page is loaded last, so the frame ends on it.
// → true only when every change was confirmed and each is confirmable.
export async function confirmOnSite({
  bridge, openPage, changes, cancelled = () => false, attempts = VERIFY_ATTEMPTS, windowMs = VERIFY_WINDOW_MS,
}) {
  const all = (changes || []).filter((c) => c && typeof c.id === 'string');
  if (!all.length || typeof openPage !== 'function') return false;
  const primary = all[0].path || '/';
  const list = all.filter(isConfirmable);
  const canVerify = typeof bridge?.verify === 'function' && typeof bridge?.waitForReady === 'function';
  if (!list.length || !canVerify) {
    // Nothing the page can prove: just show the live page.
    openPage(primary, { bust: true });
    return false;
  }
  const byPath = new Map();
  list.forEach((c) => {
    const p = c.path || '/';
    if (!byPath.has(p)) byPath.set(p, []);
    byPath.get(p).push(c);
  });
  const order = [...byPath.keys()].filter((p) => p !== primary).concat(byPath.has(primary) ? [primary] : []);
  const pending = new Set(order);
  const started = Date.now();
  let shown = null;
  let stop = false;
  for (let attempt = 0; attempt < attempts && pending.size && !stop; attempt += 1) {
    if (attempt > 0) {
      if (Date.now() - started >= windowMs) break;
      // eslint-disable-next-line no-await-in-loop
      await sleep(VERIFY_RETRY_MS);
    }
    for (const path of order) {
      if (!pending.has(path)) continue;
      if (cancelled()) return false;
      const expected = byPath.get(path);
      try {
        const ready = bridge.waitForReady(path);
        openPage(path, { bust: true });
        shown = path;
        // eslint-disable-next-line no-await-in-loop
        await ready;
        if (cancelled()) return false;
        // eslint-disable-next-line no-await-in-loop
        const results = await bridge.verify(expected);
        const byId = new Map((results || []).map((r) => [r.changeId, r]));
        if (expected.every((c) => byId.get(c.id)?.ok === true)) {
          pending.delete(path);
        } else if (expected.some((c) => { const r = byId.get(c.id); return !r || (!r.ok && (CANNOT_CONFIRM.has(r.reason) || r.reason !== 'old_content')); })) {
          stop = true; // the page can't confirm it: don't loop
          break;
        }
        // else: old_content — the old version is still being served; try again.
      } catch {
        // The page didn't load or didn't answer: the next attempt tries again.
      }
    }
  }
  if (cancelled()) return false;
  if (shown !== primary) openPage(primary, { bust: true });
  return !stop && pending.size === 0 && list.length === all.length;
}

export default function useInlinePublish({ bridge, onOpenPage, resume = null, pollMs = POLL_MS }) {
  const [stage, setStageState] = useState('edit'); // edit | review | progress | success | under_review | failed
  const [request, setRequest] = useState(null); // { id, signature, total, failedState }
  const [er, setEr] = useState(null); // the request as the server last described it
  const [accepted, setAccepted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [published, setPublished] = useState([]); // the changes of this publish (oldest first)
  const [previews, setPreviews] = useState(() => new Map()); // change id → local picture (data URL)
  const [outcome, setOutcome] = useState(null); // failed: { reason, message }
  const [confirm, setConfirm] = useState('idle'); // idle | checking | verified | unverified
  const [lostContact, setLostContact] = useState(false);
  const [undoResume, setUndoResume] = useState(false);
  const [undo, setUndo] = useState({ state: 'idle', message: '' }); // InlineUndoButton's state
  const [undoConfirm, setUndoConfirm] = useState('idle'); // idle | checking | verified | unverified
  // under_review only: the page reload after a partial publish.
  //   idle     nothing published, or no commit to wait for: reloaded at once
  //   waiting  the applied changes are being deployed: page not reloaded yet, editing stays off
  //   live     they are on the site; the page was reloaded
  //   timeout  not confirmed in time (or the page can't tell): reloaded anyway
  const [pageUpdate, setPageUpdateState] = useState('idle');

  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const openRef = useRef(onOpenPage);
  openRef.current = onOpenPage;
  const stageRef = useRef(stage);
  const requestRef = useRef(null);
  const publishedRef = useRef([]);
  const settledRef = useRef(false);
  const seenRef = useRef(false); // the server has shown this attempt publishing
  const postDoneRef = useRef(false); // POST /publish has answered
  const runRef = useRef(0); // the current on-site check (a newer one cancels it)
  const resumedRef = useRef(false); // picked up after a reload: the server knows when it started

  const setStage = useCallback((s) => { stageRef.current = s; setStageState(s); }, []);
  const pageUpdateRef = useRef('idle');
  const setPageUpdate = useCallback((s) => { pageUpdateRef.current = s; setPageUpdateState(s); }, []);
  const commitRequest = useCallback((r) => { requestRef.current = r; setRequest(r); }, []);
  const commitPublished = useCallback((list) => { publishedRef.current = list; setPublished(list); }, []);
  useEffect(() => () => { runRef.current += 1; }, []);

  // Editing in the page only while editing.
  useEffect(() => { bridgeRef.current?.setPaused?.(stage !== 'edit'); }, [stage]);

  const primaryPath = useCallback(() => publishedRef.current[0]?.path || bridgeRef.current?.currentPath || '/', []);

  const settle = useCallback((next, publisher) => {
    if (!next || settledRef.current) return;
    const cur = requestRef.current;
    if (cur && next.id && next.id !== cur.id) return;
    setEr(next);
    if (resumedRef.current) {
      const t = parseServerDate(next.publishStartedAt)?.getTime();
      if (t) setStartedAt(t);
    }
    const state = next.publishState;
    if (isPublishingState(state)) { setAccepted(true); return; }
    const changes = Array.isArray(next.changes) ? next.changes : [];
    if (changes.length) commitPublished(changes);
    if (state === 'live') {
      settledRef.current = true;
      setAccepted(true);
      bridgeRef.current?.clearAll?.();
      setConfirm('checking');
      const run = ++runRef.current;
      confirmOnSite({
        bridge: bridgeRef.current,
        openPage: (p, o) => openRef.current?.(p, o),
        changes: verifiableChanges(publishedRef.current),
        cancelled: () => runRef.current !== run,
      }).then((ok) => {
        if (runRef.current !== run) return;
        setConfirm(ok ? 'verified' : 'unverified');
        setStage('success');
      });
    } else if (state === 'needs_review') {
      settledRef.current = true;
      bridgeRef.current?.clearAll?.();
      const applied = verifiableChanges(publishedRef.current.filter((c) => c?.result?.status === 'applied'));
      if (!next.mergeCommitSha || !applied.length) {
        // Nothing was pushed: the page is as it was.
        setPageUpdate('idle');
        openRef.current?.(primaryPath(), { bust: true });
        setStage('under_review');
        return;
      }
      // Some changes were pushed but Cloudflare hasn't deployed them yet: a
      // reload now would show the old words, and editing those fails the next
      // publish (P.S. 83, 2026-10-11). Wait until the page shows the applied
      // changes, then reload it; editing stays off until then.
      setPageUpdate('waiting');
      setStage('under_review');
      const run = ++runRef.current;
      const cancelled = () => runRef.current !== run;
      confirmOnSite({
        bridge: bridgeRef.current,
        openPage: (p, o) => openRef.current?.(p, o),
        changes: applied,
        cancelled,
        attempts: REVIEW_WAIT_ATTEMPTS,
        windowMs: REVIEW_WAIT_MS,
      }).then(async (ok) => {
        if (cancelled()) return;
        const path = primaryPath();
        const b = bridgeRef.current;
        const ready = typeof b?.waitForReady === 'function' ? b.waitForReady(path).catch(() => null) : null;
        openRef.current?.(path, { bust: true });
        await ready;
        if (cancelled()) return;
        setPageUpdate(ok ? 'live' : 'timeout');
      });
    } else if (state === 'failed' || state === 'deploy_failed') {
      settledRef.current = true;
      const reason = state === 'deploy_failed'
        ? 'deploy_failed'
        : publisher?.reason || failureReasonFromChanges(changes) || null;
      if (cur) commitRequest({ ...cur, failedState: state });
      setOutcome({ reason });
      setStage('failed');
    }
  }, [commitPublished, commitRequest, primaryPath, setStage, setPageUpdate]);

  const fail = useCallback((o) => {
    settledRef.current = true;
    const cur = requestRef.current;
    if (cur) commitRequest({ ...cur, failedState: cur.failedState || 'failed' });
    setOutcome(o);
    setStage('failed');
  }, [commitRequest, setStage]);

  // Poll while publishing. The POST /publish answer normally settles the lab
  // runner's publish; the workflow's goes through the poll.
  useEffect(() => {
    if (stage !== 'progress' || !request?.id) return undefined;
    let errors = 0;
    const t = setInterval(async () => {
      if (settledRef.current) return;
      try {
        const data = await api.get(`/api/edit-requests/${request.id}`);
        errors = 0;
        setLostContact(false);
        const next = data?.editRequest;
        if (!next || settledRef.current) return;
        if (isPublishingState(next.publishState)) { seenRef.current = true; settle(next); return; }
        if (seenRef.current || postDoneRef.current) settle(next);
        // The publish call ended without starting anything.
        if (postDoneRef.current && !next.publishState && !settledRef.current) fail({ message: 'Publishing did not start.' });
      } catch {
        errors += 1;
        // Keep going: the update runs on without us. Just say we can't see it.
        if (errors >= LOST_CONTACT_AFTER) setLostContact(true);
      }
    }, pollMs);
    return () => clearInterval(t);
  }, [stage, request?.id, pollMs, settle, fail]);

  const startPublish = useCallback((id) => {
    settledRef.current = false;
    seenRef.current = false;
    postDoneRef.current = false;
    setAccepted(false);
    setLostContact(false);
    setStage('progress');
    api.post(`/api/edit-requests/${id}/publish`, {})
      .then((data) => {
        postDoneRef.current = true;
        const next = data?.editRequest;
        if (next && isPublishingState(next.publishState)) seenRef.current = true;
        settle(next, data?.publisher);
      })
      .catch((err) => {
        postDoneRef.current = true;
        if (err instanceof ApiError && err.data?.editRequest) {
          settle(err.data.editRequest, { reason: err.data.code || err.data.reason });
          if (!settledRef.current) fail({ message: publishErrorMessage(err), reason: err.data?.code || null });
          return;
        }
        if (err instanceof ApiError && err.status === 409 && err.data?.code === 'publishing') return; // already running: poll
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          fail({ message: publishErrorMessage(err), reason: err.data?.code || null });
          return;
        }
        if (err instanceof ApiError && err.status === 503) fail({ message: err.message });
        // Network drop / gateway timeout: the poll finds out what happened.
      });
  }, [settle, fail, setStage]);

  const publish = useCallback(async () => {
    const b = bridgeRef.current;
    const oldestFirst = (b?.changeList || []).slice().reverse();
    const payload = buildCreatePayload(oldestFirst, b?.uploads);
    const signature = JSON.stringify(payload.changes);
    commitPublished(payload.changes);
    setPreviews(new Map(oldestFirst
      .filter((e) => e.change.kind === 'image' && b?.uploads?.get?.(e.change.id)?.dataUrl)
      .map((e) => [e.change.id, b.uploads.get(e.change.id).dataUrl])));
    setEr(null);
    setOutcome(null);
    setConfirm('idle');
    setPageUpdate('idle');
    resumedRef.current = false;
    setStartedAt(Date.now());
    const prev = requestRef.current;
    // The same changes after a failure that changed nothing: publish that request again.
    if (prev?.id && prev.failedState === 'failed' && prev.signature === signature) {
      commitRequest({ ...prev, failedState: null });
      startPublish(prev.id);
      return;
    }
    settledRef.current = false;
    setAccepted(false);
    setStage('progress');
    try {
      // A failed attempt that is being replaced would otherwise sit in the queue.
      if (prev?.id && prev.failedState === 'failed') api.delete(`/api/edit-requests/${prev.id}`).catch(() => {});
      const data = await api.post('/api/edit-requests', payload);
      const id = data?.editRequest?.id;
      if (!id) throw new Error('No request was created');
      commitRequest({ id, signature, total: payload.changes.length, failedState: null });
      startPublish(id);
    } catch (err) {
      commitRequest(null);
      fail(err instanceof ApiError
        ? { message: publishErrorMessage(err), reason: err.data?.code || null }
        : { message: 'Could not save your changes.' });
    }
  }, [commitPublished, commitRequest, startPublish, fail, setStage, setPageUpdate]);

  // Pick up a publish (or undo) that is still running after a reload.
  const resumeFrom = useCallback((item) => {
    if (!item?.id) return;
    runRef.current += 1;
    commitRequest({ id: item.id, signature: null, total: item.changeCount || 0, failedState: null });
    commitPublished([]);
    setPreviews(new Map());
    setOutcome(null);
    setConfirm('idle');
    setPageUpdate('idle');
    setEr({ id: item.id, publishState: item.publishState, publishProgress: item.publishProgress || null, title: item.title || null });
    resumedRef.current = true;
    setStartedAt(parseServerDate(item.publishStartedAt)?.getTime() || parseServerDate(item.createdAt)?.getTime() || Date.now());
    settledRef.current = false;
    seenRef.current = true;
    postDoneRef.current = true;
    setAccepted(true);
    if (item.publishState === 'reverting') {
      settledRef.current = true;
      setUndoResume(true);
      setStage('success');
      Promise.resolve(api.get(`/api/edit-requests/${item.id}`)).then((data) => {
        const full = data?.editRequest;
        if (full && requestRef.current?.id === item.id) {
          setEr(full);
          if (Array.isArray(full.changes)) commitPublished(full.changes);
        }
      }).catch(() => {});
      return;
    }
    setStage('progress');
  }, [commitRequest, commitPublished, setStage, setPageUpdate]);

  const resumeId = resume?.id || null;
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  useEffect(() => {
    if (resumeId && stageRef.current === 'edit') resumeFrom(resumeRef.current);
  }, [resumeId, resumeFrom]);

  // After an undo: is the page back to the original wording?
  const onUndoState = useCallback((state, message) => {
    setUndo({ state, message });
    if (state !== 'reverted') return;
    const originals = invertTextChanges(publishedRef.current);
    if (!originals.length) { setUndoConfirm('unverified'); return; }
    setUndoConfirm('checking');
    const run = ++runRef.current;
    confirmOnSite({
      bridge: bridgeRef.current,
      openPage: (p, o) => openRef.current?.(p, o),
      changes: originals,
      cancelled: () => runRef.current !== run,
    }).then((ok) => { if (runRef.current === run) setUndoConfirm(ok ? 'verified' : 'unverified'); });
  }, []);

  const toReview = useCallback(() => setStage('review'), [setStage]);
  const keepEditing = useCallback(() => setStage('edit'), [setStage]);
  // A fresh round: empty list, edit mode stays on. Not while a partial
  // publish waits to reload the page (the old words would be edited).
  const makeMore = useCallback(() => {
    if (pageUpdateRef.current === 'waiting') return;
    runRef.current += 1;
    bridgeRef.current?.clearAll?.();
    commitRequest(null);
    commitPublished([]);
    setPreviews(new Map());
    setEr(null);
    setOutcome(null);
    setConfirm('idle');
    setAccepted(false);
    setUndo({ state: 'idle', message: '' });
    setUndoConfirm('idle');
    setUndoResume(false);
    setPageUpdate('idle');
    settledRef.current = false;
    setStage('edit');
  }, [commitRequest, commitPublished, setStage, setPageUpdate]);

  const busy = stage === 'progress' || undo.state === 'undoing' || undoConfirm === 'checking'
    || (stage === 'under_review' && pageUpdate === 'waiting');

  return {
    stage, request, er, accepted, startedAt, published, previews, outcome, confirm, lostContact, pageUpdate,
    undo, undoConfirm, undoResume, busy,
    toReview, keepEditing, publish, makeMore, resumeFrom, onUndoState,
  };
}
